/**
 * Utilidades para melhorar a acessibilidade em aplicações mobile
 */

/**
 * Verifica se o usuário está usando um leitor de tela
 * @returns {boolean} - Verdadeiro se um leitor de tela estiver ativo
 */
export function isScreenReaderActive() {
  // Lista conhecida de objetos que indicam leitores de tela
  const possibleIndicators = [
    // iOS
    'voiceOverRunning' in window.navigator,
    // Android
    'accessibility' in window &&
    'accessibilityEnabled' in window.accessibility,
    // Chrome
    'accessibilityController' in window,
    // Windows
    'msTextEnhanceHoverListening' in window
  ];
  
  return possibleIndicators.some(indicator => Boolean(indicator));
}

/**
 * Verifica o tamanho da fonte do sistema do usuário
 * @returns {Object} - Informações sobre o tamanho da fonte
 */
export function getAccessibilityFontInfo() {
  // Determina se há fontes grandes configuradas
  const testElement = document.createElement('div');
  testElement.style.fontSize = '1rem';
  testElement.style.position = 'absolute';
  testElement.style.visibility = 'hidden';
  testElement.textContent = 'M';
  document.body.appendChild(testElement);
  
  // Obter tamanho computado
  const fontSize = parseFloat(window.getComputedStyle(testElement).fontSize);
  document.body.removeChild(testElement);
  
  // Estimar configurações de fonte do usuário
  // 16px é o tamanho padrão na maioria dos navegadores
  const estimatedRatio = fontSize / 16;
  
  return {
    fontSize,
    estimatedRatio,
    isLargeFontEnabled: estimatedRatio > 1.2,
  };
}

/**
 * Verifica se há configurações de alto contraste ativas
 * @returns {boolean} - Verdadeiro se o modo de alto contraste estiver ativo
 */
export function isHighContrastMode() {
  // Verificação moderna usando prefers-contrast
  if (window.matchMedia('(prefers-contrast: more)').matches) {
    return true;
  }
  
  // Verificação para navegadores mais antigos
  // Cria elemento de teste e verifica configurações de cor
  const testElement = document.createElement('div');
  testElement.style.position = 'absolute';
  testElement.style.top = '-999px';
  testElement.style.backgroundColor = 'black';
  testElement.style.color = 'white';
  testElement.textContent = 'Test';
  document.body.appendChild(testElement);
  
  // Verificar cores computadas
  const computedStyle = window.getComputedStyle(testElement);
  const backgroundColor = computedStyle.backgroundColor;
  const textColor = computedStyle.color;
  
  document.body.removeChild(testElement);
  
  // Em modo de alto contraste, os navegadores podem forçar cores diferentes
  // do que as definidas no CSS, então verificamos se elas foram alteradas
  return (
    backgroundColor !== 'rgb(0, 0, 0)' || // "black" alterado
    textColor !== 'rgb(255, 255, 255)'    // "white" alterado
  );
}

/**
 * Verifica se o modo de redução de movimento está ativo
 * @returns {boolean} - Verdadeiro se o modo de redução de movimento estiver ativo
 */
export function isReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Aplica atributos ARIA apropriados a um elemento
 * @param {HTMLElement} element - Elemento para melhorar
 * @param {Object} options - Opções de acessibilidade
 */
export function enhanceElementAccessibility(element, options = {}) {
  if (!element) return;
  
  const {
    role,
    label,
    description,
    controls,
    expanded,
    selected,
    checked,
    hidden,
    required,
    errorMessage,
    isBusy,
    isDisabled,
    hasPopup,
    tabIndex
  } = options;
  
  // Definir role (função do elemento na interface)
  if (role) {
    element.setAttribute('role', role);
  }
  
  // Definir rótulo acessível
  if (label) {
    element.setAttribute('aria-label', label);
  }
  
  // Definir descrição mais detalhada
  if (description) {
    element.setAttribute('aria-description', description);
  }
  
  // ID do elemento controlado por este
  if (controls) {
    element.setAttribute('aria-controls', controls);
  }
  
  // Estado expandido (para menus, acordeões, etc.)
  if (expanded !== undefined) {
    element.setAttribute('aria-expanded', expanded.toString());
  }
  
  // Estado selecionado
  if (selected !== undefined) {
    element.setAttribute('aria-selected', selected.toString());
  }
  
  // Estado marcado (para checkboxes, etc.)
  if (checked !== undefined) {
    element.setAttribute('aria-checked', checked.toString());
  }
  
  // Estado oculto
  if (hidden !== undefined) {
    element.setAttribute('aria-hidden', hidden.toString());
  }
  
  // Campo obrigatório
  if (required !== undefined) {
    element.setAttribute('aria-required', required.toString());
  }
  
  // Mensagem de erro associada
  if (errorMessage) {
    element.setAttribute('aria-errormessage', errorMessage);
    element.setAttribute('aria-invalid', 'true');
  }
  
  // Estado de carregamento
  if (isBusy !== undefined) {
    element.setAttribute('aria-busy', isBusy.toString());
  }
  
  // Estado desativado
  if (isDisabled !== undefined) {
    element.setAttribute('aria-disabled', isDisabled.toString());
    
    // Também definir o atributo disabled para elementos de formulário
    if (isDisabled && 
        (element instanceof HTMLButtonElement || 
         element instanceof HTMLInputElement || 
         element instanceof HTMLSelectElement || 
         element instanceof HTMLTextAreaElement)) {
      element.disabled = true;
    }
  }
  
  // Indica que o elemento tem um popup
  if (hasPopup) {
    // Pode ser true ou valores específicos como 'menu', 'dialog', etc.
    const value = hasPopup === true ? 'true' : hasPopup;
    element.setAttribute('aria-haspopup', value);
  }
  
  // Ordem de tabulação
  if (tabIndex !== undefined) {
    element.tabIndex = tabIndex;
  }
}

/**
 * Melhora a acessibilidade de um conjunto de opcões (como menu, tabs, etc.)
 * @param {HTMLElement} containerElement - Elemento contendo as opcões
 * @param {Array<HTMLElement>} options - Lista de elementos de opção
 * @param {Object} ariaConfig - Configuração ARIA adicional
 */
export function setupAccessibleOptions(containerElement, options, ariaConfig = {}) {
  if (!containerElement || !options || options.length === 0) return;
  
  // Configurar container (como tablist, menu, radiogroup, etc.)
  enhanceElementAccessibility(containerElement, {
    ...ariaConfig,
    role: ariaConfig.role || 'tablist'
  });
  
  // Configurar cada opção
  options.forEach((option, index) => {
    const itemRole = ariaConfig.itemRole || 'tab';
    
    // Garantir que o item seja focável e navegável por teclado
    option.tabIndex = index === 0 ? 0 : -1;
    
    enhanceElementAccessibility(option, {
      role: itemRole,
      selected: index === 0, // Primeiro item selecionado por padrão
      // IDs para controle de painéis associados
      controls: option.getAttribute('aria-controls') || option.getAttribute('data-controls')
    });
    
    // Adicionar manipuladores de teclas para navegação
    option.addEventListener('keydown', (event) => {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          focusNextOption(options, index);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          focusPreviousOption(options, index);
          break;
        case 'Home':
          event.preventDefault();
          focusOption(options, 0);
          break;
        case 'End':
          event.preventDefault();
          focusOption(options, options.length - 1);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          option.click(); // Acionar clique para selecionar a opção
          break;
      }
    });
  });
  
  // Funções auxiliares para navegação por teclado
  function focusOption(options, index) {
    options.forEach((opt, i) => {
      opt.tabIndex = i === index ? 0 : -1;
    });
    options[index].focus();
  }
  
  function focusNextOption(options, currentIndex) {
    const nextIndex = (currentIndex + 1) % options.length;
    focusOption(options, nextIndex);
  }
  
  function focusPreviousOption(options, currentIndex) {
    const prevIndex = (currentIndex - 1 + options.length) % options.length;
    focusOption(options, prevIndex);
  }
}

/**
 * Converte um elemento em um botão totalmente acessível
 * @param {HTMLElement} element - Elemento para converter em botão
 * @param {Object} options - Opções de configuração do botão
 */
export function makeAccessibleButton(element, options = {}) {
  if (!element) return;
  
  // Garantir que o elemento tenha role="button"
  element.setAttribute('role', 'button');
  
  // Garantir que o elemento seja focável
  if (element.tabIndex === undefined || element.tabIndex < 0) {
    element.tabIndex = 0;
  }
  
  // Aplicar atributos ARIA
  enhanceElementAccessibility(element, options);
  
  // Adicionar manipuladores de teclas (Enter/Space)
  element.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      element.click();
    }
  });
  
  // Adicionar feedback tátil para dispositivos móveis
  if (options.provideTactileFeedback !== false) {
    element.addEventListener('touchstart', () => {
      element.classList.add('touch-active');
    }, { passive: true });
    
    element.addEventListener('touchend', () => {
      element.classList.remove('touch-active');
    });
  }
}

/**
 * Cria uma região de toast acessível para notificações
 * @param {Object} options - Opções de configuração
 * @returns {Object} - API para gerenciar toasts
 */
export function createAccessibleToast(options = {}) {
  const settings = {
    position: options.position || 'bottom', // 'top', 'bottom'
    duration: options.duration || 5000,
    container: options.container || document.body,
    isAlert: options.isAlert !== false, // Por padrão, é um alerta
    autoDismiss: options.autoDismiss !== false, // Auto-dismiss por padrão
    ...options
  };
  
  // Criar container de toast se não existir
  let toastContainer = document.getElementById('accessibility-toast-container');
  
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.id = 'accessibility-toast-container';
    toastContainer.setAttribute('role', 'log');
    toastContainer.setAttribute('aria-live', 'polite');
    
    // Estilos do container
    toastContainer.style.position = 'fixed';
    toastContainer.style.zIndex = '9999';
    toastContainer.style.left = '50%';
    toastContainer.style.transform = 'translateX(-50%)';
    toastContainer.style.width = 'auto';
    toastContainer.style.maxWidth = '90%';
    
    // Posicionar de acordo com a configuração
    toastContainer.style[settings.position] = '20px';
    
    // Adicionar ao DOM
    settings.container.appendChild(toastContainer);
  }
  
  // Função para criar um novo toast
  const createToast = (message, type = 'info', customOptions = {}) => {
    // Mesclar opções com padrões
    const toastOptions = {
      ...settings,
      ...customOptions
    };
    
    // Criar elemento de toast
    const toast = document.createElement('div');
    toast.className = `accessibility-toast accessibility-toast-${type}`;
    toast.setAttribute('role', toastOptions.isAlert ? 'alert' : 'status');
    
    // Estilizar toast
    toast.style.padding = '12px 16px';
    toast.style.marginBottom = '8px';
    toast.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    toast.style.color = '#ffffff';
    toast.style.borderRadius = '8px';
    toast.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
    toast.style.display = 'flex';
    toast.style.alignItems = 'center';
    toast.style.justifyContent = 'space-between';
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(20px)';
    toast.style.transition = 'opacity 0.3s, transform 0.3s';
    toast.style.borderLeft = `4px solid ${getBorderColor(type)}`;
    
    // Definir conteúdo
    const messageEl = document.createElement('div');
    messageEl.textContent = message;
    toast.appendChild(messageEl);
    
    // Adicionar botão de fechar
    if (toastOptions.showCloseButton !== false) {
      const closeButton = document.createElement('button');
      closeButton.textContent = '×';
      closeButton.style.background = 'transparent';
      closeButton.style.border = 'none';
      closeButton.style.color = '#ffffff';
      closeButton.style.fontSize = '20px';
      closeButton.style.marginLeft = '8px';
      closeButton.style.cursor = 'pointer';
      closeButton.style.padding = '0 4px';
      
      // Tornar o botão acessível
      makeAccessibleButton(closeButton, {
        label: 'Fechar notificação',
        provideTactileFeedback: true
      });
      
      closeButton.addEventListener('click', () => {
        dismissToast(toast);
      });
      
      toast.appendChild(closeButton);
    }
    
    // Adicionar ao container
    toastContainer.appendChild(toast);
    
    // Animar entrada
    setTimeout(() => {
      toast.style.opacity = '1';
      toast.style.transform = 'translateY(0)';
    }, 10);
    
    // Auto-dismiss se configurado
    if (toastOptions.autoDismiss) {
      setTimeout(() => {
        dismissToast(toast);
      }, toastOptions.duration);
    }
    
    // Retornar API para controlar este toast
    return {
      dismiss: () => dismissToast(toast),
      update: (newMessage) => {
        messageEl.textContent = newMessage;
      },
      getElement: () => toast
    };
  };
  
  // Função para remover um toast
  const dismissToast = (toast) => {
    if (!toast || !toast.parentNode) return;
    
    // Animar saída
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(-20px)';
    
    // Remover após animação
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
    }, 300);
  };
  
  // Cor da borda baseada no tipo
  function getBorderColor(type) {
    switch (type) {
      case 'success':
        return '#4caf50';
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
      default:
        return '#2196f3';
    }
  }
  
  // API pública
  return {
    /**
     * Exibe um toast informativo
     * @param {string} message - Mensagem a ser exibida
     * @param {Object} options - Opções de configuração específicas para este toast
     */
    info: (message, options = {}) => createToast(message, 'info', options),
    
    /**
     * Exibe um toast de sucesso
     * @param {string} message - Mensagem a ser exibida
     * @param {Object} options - Opções de configuração específicas para este toast
     */
    success: (message, options = {}) => createToast(message, 'success', options),
    
    /**
     * Exibe um toast de erro
     * @param {string} message - Mensagem a ser exibida
     * @param {Object} options - Opções de configuração específicas para este toast
     */
    error: (message, options = {}) => createToast(message, 'error', options),
    
    /**
     * Exibe um toast de aviso
     * @param {string} message - Mensagem a ser exibida
     * @param {Object} options - Opções de configuração específicas para este toast
     */
    warning: (message, options = {}) => createToast(message, 'warning', options),
    
    /**
     * Remove todos os toasts atuais
     */
    dismissAll: () => {
      const toasts = toastContainer.querySelectorAll('.accessibility-toast');
      toasts.forEach(toast => dismissToast(toast));
    },
    
    /**
     * Atualiza as configurações globais
     * @param {Object} newOptions - Novas opções de configuração
     */
    updateSettings: (newOptions) => {
      Object.assign(settings, newOptions);
      
      // Atualizar posição do container se necessário
      if (newOptions.position) {
        // Resetar posição anterior
        toastContainer.style.top = '';
        toastContainer.style.bottom = '';
        
        // Definir nova posição
        toastContainer.style[newOptions.position] = '20px';
      }
    }
  };
}

// Exportar todas as funções
export default {
  isScreenReaderActive,
  getAccessibilityFontInfo,
  isHighContrastMode,
  isReducedMotion,
  enhanceElementAccessibility,
  setupAccessibleOptions,
  makeAccessibleButton,
  createAccessibleToast
}; 